import { defineNuxtPlugin, useNuxtApp } from '#imports'

import {
  format as Format,
  parse as Parse,
  add as Add,
  sub as Sub,
  addMinutes,
  subMinutes,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfYear,
  startOfQuarter,
  getTime,
  isBefore,
  isValid as IsValid,
  differenceInMinutes,
} from 'date-fns'

import { enGB, enUS, es, fr, ja } from 'date-fns/locale'

import { TimeFormat } from '@/_api/models/location'

type Duration = Parameters<typeof Add>[1]

export interface DatePlugin {
  parse(date: string, format?: string): Date
  convert(date: string, inputFormat?: string, outputFormat?: string): string | null
  utc(date: Date | null): Date | null
  timezone(date: Date | null): Date | null
  local(date: Date | string): Date
  iso(date: string): Date
  timestamp(date: Date): number
  now(): number
  add(date: Date | string, duration: Duration, inputFormat?: string, outputFormat?: string): Date | string
  sub(date: Date | string, duration: Duration, inputFormat?: string, outputFormat?: string): Date | string
  minutes(total: number, format?: string): Date | string | null
  getMinutes(val: Date): number
  getStartOfDayUTC(val: Date | null): Date | null
  isBefore(a: Date, b: Date): boolean
  startOf(date: Date, unit: string): Date | undefined
  format(date: Date | null, format?: string): string | null
  getOrdinal(day: number): string
  presets: {
    readonly time: string
    readonly full: string
    readonly compact: string
    long: string
    short: string
  }
}

export const plugin: DatePlugin = {
  parse(date, format = 'yyyyMMdd') {
    return Parse(String(date), format, new Date())
  },
  convert(date, inputFormat, outputFormat) {
    const parsed = this.parse(date, inputFormat)
    return this.format(parsed, outputFormat)
  },
  utc(date) {
    if (date) {
      return addMinutes(date, date.getTimezoneOffset())
    } else {
      return null
    }
  },
  timezone(date) {
    if (date) {
      return subMinutes(date, date.getTimezoneOffset())
    } else {
      return null
    }
  },
  local(date) {
    let offset = 0
    try {
      const nuxtApp = useNuxtApp()
      offset = (nuxtApp as any).$auth?.info?.location?.timeZoneOffset || 0
    } catch {
      // useNuxtApp() not available outside Nuxt context
    }
    const utc = new Date(new Date(date).toUTCString().slice(0, -4))
    const local = this.add(utc, { seconds: offset }) as Date

    return local
  },
  iso(date) {
    return new Date(date)
  },
  timestamp(date) {
    return Math.floor(getTime(date) / 1000)
  },
  now() {
    return Date.now()
  },
  add(date, duration, inputFormat?, outputFormat?) {
    if (inputFormat) {
      date = this.parse(date as string, inputFormat)
    }

    date = Add(date as Date, duration)

    if (outputFormat) {
      date = this.format(date as Date, outputFormat) as string
    }

    return date
  },
  sub(date, duration, inputFormat?, outputFormat?) {
    if (inputFormat) {
      date = this.parse(date as string, inputFormat)
    }

    date = Sub(date as Date, duration)

    if (outputFormat) {
      date = this.format(date as Date, outputFormat) as string
    }

    return date
  },
  minutes(total, format?) {
    const hours = Math.floor(total / 60)
    const minutes = total % 60

    const date = new Date(0, 0, 0, hours, minutes)

    const fmt = format ?? this.presets.time
    if (fmt) {
      return this.format(date, fmt)
    }

    return date
  },
  getMinutes(val) {
    const date = startOfDay(val)
    const time = differenceInMinutes(val, date)

    return time
  },
  getStartOfDayUTC(val) {
    if (val) {
      const utc = startOfDay(val)
      const date = Sub(utc, { minutes: utc.getTimezoneOffset() })

      return date
    } else {
      return null
    }
  },
  isBefore(a, b) {
    return isBefore(a, b)
  },
  startOf(date, unit) {
    if (['day', 'days'].includes(unit)) {
      return startOfDay(date)
    }
    if (['week', 'weeks'].includes(unit)) {
      return startOfWeek(date)
    }
    if (['month', 'months'].includes(unit)) {
      return startOfMonth(date)
    }
    if (['quarter', 'quarters'].includes(unit)) {
      return startOfQuarter(date)
    }
    if (['year', 'years'].includes(unit)) {
      return startOfYear(date)
    }
  },
  presets: {
    get time() {
      try {
        const nuxtApp = useNuxtApp()
        const format = (nuxtApp as any).$auth?.info?.location?.timeFormat
        if (format && format === TimeFormat.twentyFourHour) {
          return 'HH:mm'
        }
      } catch {
        // useNuxtApp() not available outside Nuxt context
      }
      return 'h:mm a'
    },
    get full() {
      return `EEEE, MMMM do yyyy ${this.time}`
    },
    get compact() {
      return `EEE, do MMM yy ${this.time}`
    },
    long: 'EEEE, MMMM do yyyy',
    short: 'dd/MM/yyyy',
  },
  // Placeholder — assigned in plugin setup below
  format: null as any,
  getOrdinal: null as any,
}

const localeMapping: Record<string, any> = {
  en: enGB,
  'en-US': enUS,
  es,
  fr,
  ja,
}

export default defineNuxtPlugin((nuxtApp) => {
  const format = (date: Date | null, fmt: string = 'yyyyMMdd'): string | null => {
    if (date && IsValid(date)) {
      const localeCode = (nuxtApp as any).$i18n?.locale || 'en'
      const locale = localeMapping[localeCode] || enUS

      return Format(date, fmt, { locale })
    } else {
      return null
    }
  }

  const getOrdinal = (day: number): string => {
    const locale = (nuxtApp as any).$i18n?.locale || 'en'
    const ordinalRules = new Intl.PluralRules(locale, { type: 'ordinal' })
    const category = ordinalRules.select(day)
    const dayLabel = (nuxtApp as any).$i18n?.t?.('datetime.ordinals.' + category, { number: day }) || `${day}`
    return dayLabel
  }

  Object.assign(plugin, { format, getOrdinal })

  nuxtApp.provide('date', plugin)
})
